import type { LessonPathProgress, LessonPathType } from '@kanjido/database'
import Link from 'next/link'

// Forward affordance OR Resting Card line — the persistent CTA pattern at the
// foot of every reflection surface (Learn-C / Review-B / Quiz-D / Challenge-C).
//
// Continue variant: bottom-fixed, centered within max-w-2xl, vermilion fill.
// Each surface's ONE at-rest vermilion placement (§7 budget 1/2). Rendered as
// a Link, not a button — the navigation IS the whole point; no client-side
// handler stands between intent and target.
//
// Resting variant: NO bottom-fixed bar, NO vermilion. The italic-serif line
// sits inline at the column foot (§3.8 Resting Card). The page ends quietly —
// the way has nothing to ask of the user. Returning the same pattern for both
// variants would import the wrong register: vermilion at rest under a "rest"
// statement is the loud-celebration trap §12.2 explicitly bans.

export type ForwardAffordanceShape =
    | { kind: 'continue'; href: string; label: string }
    | { kind: 'resting'; line: string }

// Resolver-input shape shared across path-specific next-path resolvers.
export type PathProgressByType = Record<LessonPathType, LessonPathProgress>

export function ForwardAffordance({
    affordance,
}: {
    affordance: ForwardAffordanceShape
}) {
    if (affordance.kind === 'resting') {
        return (
            <p className="mt-12 text-center font-serif text-lg leading-relaxed text-body-fg italic md:mt-16 md:text-xl">
                {affordance.line}
            </p>
        )
    }

    return (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-rule-soft bg-paper/95 bg-linear-to-t from-paper to-transparent px-5 pt-4 pb-safe backdrop-blur-sm md:px-8">
            <div className="mx-auto flex w-full max-w-2xl justify-center">
                <Link
                    href={affordance.href}
                    aria-label={affordance.label}
                    className="inline-flex h-12 w-full max-w-xs items-center justify-center gap-1.5 rounded-sm bg-action font-serif text-base font-medium tracking-wide text-action-on outline-focus-ring transition-colors duration-200 hover:bg-action-hover focus-visible:outline-2 focus-visible:outline-offset-4 active:translate-y-px active:bg-action-pressed motion-reduce:transition-none motion-reduce:active:translate-y-0"
                >
                    <span>{affordance.label}</span>
                    <span aria-hidden>→</span>
                </Link>
            </div>
        </div>
    )
}
